import { CRM_ADMIN_API_URL, GATEWAY_API_URL } from '@admin-panel/config/env';
import { MetaQuery } from '@refinedev/core';
import CrudApiClient from './crudapiclient';
import { axiosInstance } from './axios';

export type CrmAdminSearchResponse<T = any> = {
  data: Array<T>;
  page: number;
  size: number;
  total: number;
  total_pages?: number;
  has_next?: boolean;
};

export type CrmAdminApiClientOptions = {
  definitionPath: string;
  runsPath?: string;
  summaryPath?: string;
  suppressEmptySearch?: boolean;
};

const normalizeBaseUrl = (value: string) =>
  value
    .trim()
    .replace(/\/api\/admin\/?$/, '')
    .replace(/\/api\/?$/, '')
    .replace(/\/$/, '');

export const getCrmAdminRootUrl = () =>
  normalizeBaseUrl(CRM_ADMIN_API_URL || GATEWAY_API_URL || '');

const joinPath = (path: string) => {
  const root = getCrmAdminRootUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${root}${normalizedPath}`;
};

class CrmAdminApiClient extends CrudApiClient {
  private runsBaseURL?: string;
  private summaryBaseURL?: string;
  private suppressEmptySearch: boolean;

  constructor(options: CrmAdminApiClientOptions) {
    super(joinPath(options.definitionPath));
    this.runsBaseURL = options.runsPath ? joinPath(options.runsPath) : undefined;
    this.summaryBaseURL = options.summaryPath ? joinPath(options.summaryPath) : undefined;
    this.suppressEmptySearch = Boolean(options.suppressEmptySearch);
  }

  private unwrapResponse<T>(response: any): T {
    return (response?.data?.data ?? response?.data ?? response) as T;
  }

  async search<T = any, Q = any>(
    query: Q,
    page: number,
    size: number,
    meta: MetaQuery | undefined,
  ): Promise<Array<T> & CrmAdminSearchResponse<T>> {
    try {
      const hasMeaningfulSearch =
        !this.suppressEmptySearch ||
        Boolean(
          Array.isArray((query as { filters?: Array<{ value?: unknown }> })?.filters) &&
            (query as { filters?: Array<{ value?: unknown }> }).filters?.some(
              filter =>
                filter.value !== undefined &&
                filter.value !== null &&
                `${filter.value}`.trim() !== '',
            ),
        );

      if (!hasMeaningfulSearch) {
        return Object.assign([], {
          data: [],
          page,
          size,
          total: 0,
          total_pages: 0,
          has_next: false,
        }) as Array<T> & CrmAdminSearchResponse<T>;
      }

      const response = await axiosInstance.post(
        `${this.baseURL}/search?page=${page}&size=${size}`,
        query,
        this.getRequestConfig(meta),
      );
      const payload = this.unwrapResponse<CrmAdminSearchResponse<T>>(response);
      const rows = Array.isArray(payload?.data) ? payload.data : [];
      const enrichedRows = Object.assign(rows, {
        data: rows,
        page: Number(payload?.page ?? page),
        size: Number(payload?.size ?? size),
        total: Number(payload?.total ?? rows.length),
        total_pages: payload?.total_pages,
        has_next: payload?.has_next,
      });
      return enrichedRows as Array<T> & CrmAdminSearchResponse<T>;
    } catch (error) {
      console.error(`Error searching CRM entities with Query:`, query, error);
      throw error;
    }
  }

  async findById<T = any>(id: string | number, meta: MetaQuery | undefined): Promise<T> {
    try {
      const response = await axiosInstance.get(
        `${this.baseURL}/${id}`,
        this.getRequestConfig(meta),
      );
      return this.unwrapResponse<T>(response);
    } catch (error) {
      console.error(`Error fetching CRM entity with ID ${id}:`, error);
      throw error;
    }
  }

  async create<T = any>(payload: Partial<T>, meta: MetaQuery | undefined): Promise<T> {
    try {
      const response = await axiosInstance.post(
        `${this.baseURL}/`,
        payload,
        this.getRequestConfig(meta),
      );
      return this.unwrapResponse<T>(response);
    } catch (error) {
      console.error(`Error creating CRM entity:`, error);
      throw error;
    }
  }

  async updateById<T = any>(
    id: string | number,
    data: Partial<T>,
    meta: MetaQuery | undefined,
  ): Promise<T> {
    try {
      const response = await axiosInstance.put(
        `${this.baseURL}/${id}`,
        data,
        this.getRequestConfig(meta),
      );
      return this.unwrapResponse<T>(response);
    } catch (error) {
      console.error(`Error updating CRM entity with ID ${id}:`, error);
      throw error;
    }
  }

  async listRuns<T = any>(
    id: string | number,
    page: number,
    size: number,
    options: { runAtFrom?: string; runAtTo?: string } | undefined,
    meta: MetaQuery | undefined,
  ): Promise<CrmAdminSearchResponse<T>> {
    try {
      if (!this.runsBaseURL) {
        throw new Error('runs path is not configured for this CRM admin client');
      }
      const searchParams = new URLSearchParams();
      searchParams.set('page', `${page}`);
      searchParams.set('size', `${size}`);
      if (options?.runAtFrom) {
        searchParams.set('run_at_from', options.runAtFrom);
      }
      if (options?.runAtTo) {
        searchParams.set('run_at_to', options.runAtTo);
      }
      const response = await axiosInstance.get(
        `${this.runsBaseURL}/${id}/runs?${searchParams.toString()}`,
        this.getRequestConfig(meta),
      );
      return this.unwrapResponse<CrmAdminSearchResponse<T>>(response);
    } catch (error) {
      console.error(`Error listing CRM runs for id=${id}:`, error);
      throw error;
    }
  }

  async getRunSummary<T = any>(runId: string, meta: MetaQuery | undefined): Promise<T> {
    try {
      if (!this.summaryBaseURL) {
        throw new Error('summary path is not configured for this CRM admin client');
      }
      const response = await axiosInstance.get(
        `${this.summaryBaseURL}/${runId}/summary`,
        this.getRequestConfig(meta),
      );
      return this.unwrapResponse<T>(response);
    } catch (error) {
      console.error(`Error loading CRM run summary for runId=${runId}:`, error);
      throw error;
    }
  }

  async previewRunRetry<T = any, P = any>(
    runId: string,
    payload: P,
    meta: MetaQuery | undefined,
  ): Promise<T> {
    try {
      if (!this.summaryBaseURL) {
        throw new Error('summary path is not configured for this CRM admin client');
      }
      const response = await axiosInstance.post(
        `${this.summaryBaseURL}/${runId}/retry/preview`,
        payload,
        this.getRequestConfig(meta),
      );
      return this.unwrapResponse<T>(response);
    } catch (error) {
      console.error(`Error previewing CRM retry for runId=${runId}:`, error);
      throw error;
    }
  }

  async createRunRetry<T = any, P = any>(
    runId: string,
    payload: P,
    meta: MetaQuery | undefined,
  ): Promise<T> {
    try {
      if (!this.summaryBaseURL) {
        throw new Error('summary path is not configured for this CRM admin client');
      }
      const response = await axiosInstance.post(
        `${this.summaryBaseURL}/${runId}/retry`,
        payload,
        this.getRequestConfig(meta),
      );
      return this.unwrapResponse<T>(response);
    } catch (error) {
      console.error(`Error creating CRM retry for runId=${runId}:`, error);
      throw error;
    }
  }
}

export const createCrmAdminApiClient = (options: CrmAdminApiClientOptions) =>
  new CrmAdminApiClient(options);

export default CrmAdminApiClient;
